import React from "react";
import { Stack, Autocomplete, TextField, Grid } from "@mui/material";
import { NormalText } from "src/components/styled/text.styled";
import useRedux from "src/redux/useRedux";
import { getAllStore, ListStoreData } from "src/redux/storelist";

interface FilterStoreProps {
  value: any;
  onChange: any;
  filterType?: "table" | "form" | "report";
  stores?: ListStoreData[];
}

const FilterStore = ({
  value,
  onChange,
  filterType = "table",
  stores: propStores,
}: FilterStoreProps) => {
  const { thunkDispatch } = useRedux();
  const [stores, setStores] = React.useState<ListStoreData[]>([]);

  React.useEffect(() => {
    if (propStores && propStores.length > 0) {
      setStores(propStores);
    } else {
      thunkDispatch(getAllStore({ limit: 0, offset: 0 }))
        .unwrap()
        .then((res) => {
          if (res && res.status === "success") {
            setStores(res.data.data);
          }
        })
        .catch((err) => {
          console.error("Error fetching stores:", err);
        });
    }
  }, [propStores, thunkDispatch]);

  const allStoresOption = { id: 0, store_name: "Semua Toko" } as ListStoreData;

  const selectedStore = React.useMemo((): ListStoreData => {
    if (!stores || stores.length === 0) {
      console.log("- No stores available");
      return allStoresOption;
    }

    if (!value) {
      console.log("- No value provided");
      return allStoresOption;
    }

    if ((value.id === 0 || String(value.id) === "0") || 
        (value.store_name && value.store_name.toLowerCase() === "semua toko")) {
      return allStoresOption;
    }

    let foundStore: ListStoreData | undefined;

    if (value.store_name && typeof value.store_name === 'string') {
      foundStore = stores.find((store) => 
        store.store_name?.trim().toLowerCase() === value.store_name?.trim().toLowerCase()
      );
    }

    if (!foundStore && value.id && value.id !== 0) {
      foundStore = stores.find((store) => store.id === Number(value.id));
    }

    if (!foundStore && filterType === "table" && typeof value === 'string' && value !== "") {
      const numericValue = Number(value);
      if (numericValue === 0) {
        return allStoresOption;
      }
      foundStore = stores.find((store) => store.id === numericValue);
    }

    return foundStore || allStoresOption;
  }, [stores, value, filterType]);

  const isOptionEqualToValue = (option: ListStoreData, value: ListStoreData) => {
    if (!option || !value) return false;
    if (option.id === 0 && (value.id === 0 || String(value.id) === "0")) {
      return true;
    }
    if (option.id === value.id) {
      return true;
    }
    if (option.store_name && value.store_name) {
      return option.store_name.trim().toLowerCase() === value.store_name.trim().toLowerCase();
    }
    
    return false;
  };

  if (filterType === "table") {
    return (
      <Stack direction={"column"} alignItems={"start"} spacing={1}>
        <NormalText textAlign="start" fontSize={"14px"}>
          Filter Berdasarkan Toko
        </NormalText>
        <Autocomplete
          id="combo-box-store-list"
          options={[allStoresOption, ...(stores && stores.length > 0 ? stores : [])]}
          getOptionLabel={(option) => option.store_name || ""}
          onChange={(event: any, newValue: any) => {
            const selectedValue = newValue || allStoresOption;
            onChange(selectedValue);
          }}
          onInputChange={(event, newInputValue, reason) => {
            if ((reason === "clear" || reason === "reset") && (!newInputValue || newInputValue.trim() === "")) {
              onChange(allStoresOption);
            }
          }}
          disableClearable
          value={selectedStore}
          isOptionEqualToValue={isOptionEqualToValue}
          sx={{ width: "250px" }}
          renderInput={(params) => (
            <TextField
              {...params}
              style={{ fontSize: 12 }}
              size="small"
              placeholder="Pilih Toko"
            />
          )}
        />
      </Stack>
    );
  }

  if (filterType === "report") {
    return (
      <Grid container margin={"20px 0 0 0"}>
        <Grid item xs={12} sm={12} md={2}>
          <NormalText fontSize="15px" fontWeight="bold">
            Pilih Toko
          </NormalText>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Autocomplete
            id="combo-box-store-list-report"
            options={[allStoresOption, ...(stores && stores.length > 0 ? stores : [])]}
            getOptionLabel={(option) => option.store_name || ""}
            value={selectedStore}
            onChange={(event: any, newValue: any) => {
              const selectedValue = newValue || allStoresOption;
              onChange(selectedValue);
            }}
            onInputChange={(event, newInputValue, reason) => {
              if ((reason === "clear" || reason === "reset") && (!newInputValue || newInputValue.trim() === "")) {
                onChange(allStoresOption);
              }
            }}
            disableClearable
            isOptionEqualToValue={isOptionEqualToValue}
            sx={{ width: "250px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                style={{ fontSize: 12 }}
                size="small"
                placeholder="Pilih Toko"
              />
            )}
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container margin={"20px 0 0 0"}>
      <Grid item xs={12} sm={12} md={2}>
        <NormalText fontSize="15px" fontWeight="bold">
          Pilih Toko
        </NormalText>
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <Autocomplete
          id="combo-box-store-list-form"
          options={[allStoresOption, ...(stores && stores.length > 0 ? stores : [])]}
          getOptionLabel={(option) => option.store_name || ""}
          onChange={(event: any, newValue: any) => {
            onChange(newValue || null);
          }}
          value={selectedStore || allStoresOption}
          disableClearable
          isOptionEqualToValue={isOptionEqualToValue}
          sx={{ width: "250px" }}
          renderInput={(params) => (
            <TextField
              {...params}
              style={{ fontSize: 12 }}
              size="small"
              placeholder="Pilih Toko"
            />
          )}
        />
      </Grid>
    </Grid>
  );
};

export default FilterStore;
